import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchProducts } from '../../services/product';
import { Product } from '../../services/product';

interface ProductsState {
  products: Product[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  hasFetched: boolean;
}

const initialState: ProductsState = {
  products: [],
  loading: false,
  page: 1,
  pageSize: 10,
  total: 0,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  searchQuery: '',
  hasFetched: false,
};

export const fetchProductsThunk = createAsyncThunk(
  'products/fetchProducts',
  async (_, { getState }) => {
    const state = getState() as any;
    const {
      page,
      pageSize,
      sortBy,
      sortOrder,
      searchQuery,
    } = state.products;

    const response = await fetchProducts({
      page,
      pageSize,
      sortBy,
      sortOrder,
      searchQuery: searchQuery || undefined,
    });

    const { totalCount, results } = response.result.data;

    return {
      products: results,
      total: totalCount,
    };
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setPage(state, action) {
      state.page = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setSortBy(state, action) {
      state.sortBy = action.payload;
    },
    setSortOrder(state, action) {
      state.sortOrder = action.payload;
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    resetProductsState() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProductsThunk.pending, state => {
        state.loading = true;
      })
      .addCase(fetchProductsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.total = action.payload.total;
        state.hasFetched = true;
      })
      .addCase(fetchProductsThunk.rejected, state => {
        state.loading = false;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  resetProductsState,
} = productSlice.actions;

export default productSlice.reducer;
